<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import VCardBubble from '../VCard.vue';
import { parseVCardContent } from '../vcard.mjs';
import SeamChatCallBubble from '../SeamChatCall.vue';
import { parseLegacySeamCallContent, parseSeamCallContent } from '../vcard.mjs';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const vCards = computed(() => parseVCardContent(renderContent.value));
const hasVCard = computed(() => vCards.value.length > 0);
const seamCall = computed(() => parseSeamCallContent(renderContent.value) || parseLegacySeamCallContent(renderContent.value));
const hasSeamCall = computed(() => Boolean(seamCall.value));

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble
    :class="hasVCard || hasSeamCall ? 'p-2' : 'px-4 py-3'"
    data-bubble-name="text"
  >
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <SeamChatCallBubble v-if="hasSeamCall" :call="seamCall" />
      <VCardBubble v-else-if="hasVCard" :cards="vCards" />
      <FormattedContent v-else-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations && !hasVCard && !hasSeamCall"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
